import { useState } from "react";
import { Rocket, Check, ChefHat, PenTool, Truck, GraduationCap, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EMIRATES } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SubmitBar } from "../components/SubmitBar";

const SUPPORT_ICONS = [PenTool, ChefHat, Truck, GraduationCap];

export default function RestaurantOpening() {
  const { t, lang } = useLang();
  const r = t.opening;

  const [form, setForm] = useState({
    concept_name: "",
    concept_type: "casual_dining",
    cuisine: "",
    target_open_date: "",
    seats_capacity: "",
    location_emirate: "dubai",
    investment_range: "150k_500k",
    stage: "concept",
    needs: [], // array
    contact_name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleNeed = (n) => {
    setForm((p) => ({
      ...p,
      needs: p.needs.includes(n) ? p.needs.filter((x) => x !== n) : [...p.needs, n],
    }));
  };

  const payload = {
    name: form.contact_name,
    phone: form.phone,
    project_type: form.concept_type === "cafe" ? "cafe" : "restaurant",
    emirate: form.location_emirate,
    message: `Restaurant Opening: ${form.concept_name || "Unnamed"} (${form.concept_type})`,
    email: form.email,
    company_name: form.concept_name,
    preferred_contact: "whatsapp",
  };
  const extra = {
    cuisine: form.cuisine,
    target_open_date: form.target_open_date,
    seats_capacity: form.seats_capacity,
    investment_range: form.investment_range,
    stage: form.stage,
    needs: form.needs,
    notes: form.notes,
  };

  return (
    <div data-testid="opening-page">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-[#ef4444] via-[#dc2626] to-[#7f1d1d] text-white overflow-hidden">
        <div className="absolute top-0 start-0 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 end-0 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl pointer-events-none" />

        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 border border-white/25 mb-6 backdrop-blur">
            <Rocket className="w-7 h-7" />
          </div>
          <div className="inline-block text-[10px] tracking-[0.35em] text-red-100 font-semibold mb-4 uppercase">
            {r.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 max-w-3xl mx-auto">
            {r.title}
          </h1>
          <p className="text-red-50 text-lg leading-relaxed max-w-2xl mx-auto mb-8">{r.subtitle}</p>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {r.chips.map((chip, i) => (
              <div
                key={i}
                className="inline-flex items-center gap-1.5 bg-white/10 border border-white/25 backdrop-blur rounded-full px-4 py-2 text-sm"
              >
                <Sparkles className="w-3.5 h-3.5 text-yellow-200" />
                {chip}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {r.pillarsEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] leading-tight">{r.pillarsTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {r.pillars.map((p, i) => {
              const Icon = SUPPORT_ICONS[i] || PenTool;
              return (
                <div key={i} data-testid={`ro-pillar-${i}`} className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 flex items-center justify-center mb-4 border border-red-100">
                    <Icon className="w-6 h-6 text-[#ef4444]" />
                  </div>
                  <h3 className="font-display text-base text-[#0891b2] mb-2">{p.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Wizard */}
      <section id="apply" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0f172a] text-white p-6">
              <div className="inline-block text-[10px] tracking-[0.3em] text-red-300 font-semibold mb-2 uppercase">
                {r.formEyebrow}
              </div>
              <h2 className="font-display text-2xl">{r.formTitle}</h2>
              <p className="text-white/70 text-sm mt-1">{r.formSubtitle}</p>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              {/* Concept */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100">
                {r.section1}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={r.conceptName}>
                  <Input value={form.concept_name} onChange={(e) => set("concept_name", e.target.value)} data-testid="ro-cname" className="h-11" placeholder={r.conceptNameHint} />
                </Field>
                <Field label={r.conceptType} required>
                  <Select value={form.concept_type} onValueChange={(v) => set("concept_type", v)}>
                    <SelectTrigger data-testid="ro-ctype" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["fine_dining", "casual_dining", "fast_casual", "cafe", "cloud_kitchen", "bakery", "food_truck", "other"].map((k) => (
                        <SelectItem key={k} value={k}>{r.conceptTypes[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={r.cuisine}>
                  <Input value={form.cuisine} onChange={(e) => set("cuisine", e.target.value)} data-testid="ro-cuisine" className="h-11" placeholder={r.cuisineHint} />
                </Field>
                <Field label={r.seats}>
                  <Input dir="ltr" value={form.seats_capacity} onChange={(e) => set("seats_capacity", e.target.value)} data-testid="ro-seats" className="h-11" placeholder="60" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={r.targetDate}>
                  <Input type="date" value={form.target_open_date} onChange={(e) => set("target_open_date", e.target.value)} data-testid="ro-date" className="h-11" />
                </Field>
                <Field label={r.location} required>
                  <Select value={form.location_emirate} onValueChange={(v) => set("location_emirate", v)}>
                    <SelectTrigger data-testid="ro-loc" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {/* Investment */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100 pt-4">
                {r.section2}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={r.investment}>
                  <Select value={form.investment_range} onValueChange={(v) => set("investment_range", v)}>
                    <SelectTrigger data-testid="ro-inv" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["under_150k", "150k_500k", "500k_1m", "1m_3m", "over_3m"].map((k) => (
                        <SelectItem key={k} value={k}>{r.investmentRanges[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={r.currentStage}>
                  <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                    <SelectTrigger data-testid="ro-stage" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["idea", "concept", "design", "fitout", "pre_opening"].map((k) => (
                        <SelectItem key={k} value={k}>{r.stages[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {/* Needs multi-select */}
              <Field label={r.needsLabel}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {["license", "design", "equipment", "install", "training", "marketing"].map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleNeed(k)}
                      data-testid={`ro-need-${k}`}
                      className={`text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all border ${
                        form.needs.includes(k)
                          ? "bg-[#ef4444] text-white border-[#ef4444] shadow-md"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#ef4444]"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${form.needs.includes(k) ? "bg-white border-white" : "border-slate-300"}`}>
                        {form.needs.includes(k) && <Check className="w-3 h-3 text-[#ef4444]" />}
                      </div>
                      {r.needs[k]}
                    </button>
                  ))}
                </div>
              </Field>

              {/* Contact */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pb-2 border-b border-slate-100 pt-4">
                {r.section3}
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label={r.contactName} required>
                  <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="ro-name" className="h-11" />
                </Field>
                <Field label={r.phone} required>
                  <Input required dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ro-phone" className="h-11" placeholder="+971 4-288-2777" />
                </Field>
                <Field label={r.email}>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ro-email" className="h-11" />
                </Field>
              </div>

              <Field label={r.notes}>
                <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ro-notes" placeholder={r.notesHint} />
              </Field>

              <div className="pt-4 border-t border-slate-100">
                <SubmitBar payload={payload} source="restaurant_opening" submitLabel={r.submit} extraData={extra} />
                <p className="text-xs text-slate-500 mt-3">{r.formFooter}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
